<template>
  <div class="home">
    <!-- Hero Section with softer gradient -->
    <section class="hero rounded-bottom-xl">
      <v-container>
        <v-row align="center" justify="center" class="py-16">
          <v-col cols="12" md="6" class="text-center text-md-left">
            <h1 class="text-h2 font-weight-medium mb-4 text-white">Welcome to RankCrew</h1>
            <h2 class="text-h5 mb-6 text-white opacity-high">Your AI-powered SEO content creation service</h2>
            <p class="text-body-1 mb-8 text-white opacity-medium">
              RankCrew uses advanced AI agents to create SEO-optimized blog posts for your website.
              Configure your topics, set up a publishing calendar, and let our AI handle the rest.
            </p>
            <v-btn
              color="white"
              size="large"
              to="/register"
              class="mr-4 rounded-xl"
              variant="elevated"
              elevation="1"
            >
              <span class="text-primary">Get Started</span>
            </v-btn>
            <v-btn
              variant="outlined"
              color="white"
              size="large"
              to="/pricing"
              class="rounded-xl"
            >
              <span class="text-white">View Pricing</span>
            </v-btn>
          </v-col>
          <v-col cols="12" md="6" class="d-none d-md-flex justify-center">
            <v-img
              src="/images/agents_seo.png"
              max-width="500"
              class="rounded-xl elevation-3"
              alt="RankCrew AI Agents"
            ></v-img>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Features Section with refined cards -->
    <section class="features py-16 bg-white">
      <v-container>
        <h2 class="text-h4 text-center mb-12 text-black font-weight-medium">Key Features</h2>
        <v-row>
          <v-col cols="12" md="4">
            <v-card class="h-100 rounded-xl" variant="elevated" elevation="1">
              <v-card-item>
                <template v-slot:prepend>
                  <v-icon color="primary" size="large">mdi-robot</v-icon>
                </template>
                <v-card-title class="text-black">AI Agents</v-card-title>
              </v-card-item>
              <v-card-text class="text-black">
                <p>Our AI agents create high-quality, SEO-optimized blog posts tailored to your industry and target keywords.</p>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card class="h-100 rounded-xl" variant="elevated" elevation="1">
              <v-card-item>
                <template v-slot:prepend>
                  <v-icon color="primary" size="large">mdi-web</v-icon>
                </template>
                <v-card-title class="text-black">CMS Integration</v-card-title>
              </v-card-item>
              <v-card-text class="text-black">
                <p>Seamless integration with WordPress and Drupal. Your content is automatically published to your website.</p>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card class="h-100 rounded-xl" variant="elevated" elevation="1">
              <v-card-item>
                <template v-slot:prepend>
                  <v-icon color="primary" size="large">mdi-translate</v-icon>
                </template>
                <v-card-title class="text-black">Multilingual Support</v-card-title>
              </v-card-item>
              <v-card-text class="text-black">
                <p>Create content in any language. Our AI adapts to your target audience and regional SEO requirements.</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- AI Agents Section with simple, clean design -->
    <section class="agents-section py-16 bg-grey-lighten-4 rounded-xl mx-4 my-8">
      <v-container>
        <h2 class="text-h4 text-center mb-6 text-black font-weight-medium">Our AI Agents</h2>
        <p class="text-center text-body-1 mb-12 mx-auto" style="max-width: 700px">
          Powered by OpenAI's cutting-edge GPT 5.0 model, our specialized AI agents work together to create exceptional content.
        </p>
        
        <v-row class="mt-4">
          <v-col cols="12" sm="6" lg="4">
            <v-card class="agent-card h-100 rounded-xl pa-4 border" flat>
              <div class="d-flex flex-column align-center text-center">
                <v-avatar color="#e8f5e9" class="mb-4" size="64">
                  <v-icon color="primary" size="32">mdi-lightbulb-outline</v-icon>
                </v-avatar>
                <h3 class="text-h6 mb-2">Idea Planner</h3>
                <p class="text-body-2">Researches potential article topics based on industry trends and audience interests.</p>
              </div>
            </v-card>
          </v-col>
          
          <v-col cols="12" sm="6" lg="4">
            <v-card class="agent-card h-100 rounded-xl pa-4 border" flat>
              <div class="d-flex flex-column align-center text-center">
                <v-avatar color="#e8f5e9" class="mb-4" size="64">
                  <v-icon color="primary" size="32">mdi-file-tree-outline</v-icon>
                </v-avatar>
                <h3 class="text-h6 mb-2">Content Planner</h3>
                <p class="text-body-2">Selects high-potential articles and develops optimal SEO structure with keywords.</p>
              </div>
            </v-card>
          </v-col>
          
          <v-col cols="12" sm="6" lg="4">
            <v-card class="agent-card h-100 rounded-xl pa-4 border" flat>
              <div class="d-flex flex-column align-center text-center">
                <v-avatar color="#e8f5e9" class="mb-4" size="64">
                  <v-icon color="primary" size="32">mdi-pencil</v-icon>
                </v-avatar>
                <h3 class="text-h6 mb-2">Writer Agent</h3>
                <p class="text-body-2">Creates engaging, SEO-optimized content with the perfect tone for your audience.</p>
              </div>
            </v-card>
          </v-col>
        </v-row>
        
        <v-row class="mt-4">
          <v-col cols="12" sm="6" lg="4" offset-lg="2">
            <v-card class="agent-card h-100 rounded-xl pa-4 border" flat>
              <div class="d-flex flex-column align-center text-center">
                <v-avatar color="#e8f5e9" class="mb-4" size="64">
                  <v-icon color="primary" size="32">mdi-format-text-wrapping-overflow</v-icon>
                </v-avatar>
                <h3 class="text-h6 mb-2">Style Agent</h3>
                <p class="text-body-2">Refines content to match your tone of voice and creates fluid, easy-to-read blog posts.</p>
              </div>
            </v-card>
          </v-col>
          
          <v-col cols="12" sm="6" lg="4">
            <v-card class="agent-card h-100 rounded-xl pa-4 border" flat>
              <div class="d-flex flex-column align-center text-center">
                <v-avatar color="#e8f5e9" class="mb-4" size="64">
                  <v-icon color="primary" size="32">mdi-translate</v-icon>
                </v-avatar>
                <h3 class="text-h6 mb-2">Translator</h3>
                <p class="text-body-2">Provides accurate translations that maintain meaning and SEO value across languages.</p>
              </div>
            </v-card>
          </v-col>
        </v-row>
        
        <v-row class="mt-8">
          <v-col cols="12" md="10" lg="8" class="mx-auto">
            <v-card class="manager-card rounded-xl pa-6" color="primary" theme="dark" style="background: linear-gradient(135deg, #2E7D32 0%, #81C784 100%) !important;">
              <div class="d-flex flex-column flex-md-row">
                <v-avatar color="white" size="72" class="mb-4 mb-md-0 mx-auto mx-md-0 me-md-6">
                  <v-icon color="primary" size="40">mdi-account-supervisor</v-icon>
                </v-avatar>
                <div class="text-center text-md-left">
                  <h3 class="text-h5 font-weight-medium mb-2">SEO Manager</h3>
                  <p class="text-body-1">The overseer agent that coordinates all other agents, ensuring they work together seamlessly for optimal results. It monitors the entire content creation pipeline and makes adjustments when necessary.</p>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- How It Works Section with elegant minimalist design -->
    <section class="how-it-works py-16 bg-white rounded-xl mx-4 my-8">
      <v-container>
        <h2 class="text-h4 text-center mb-16 text-black font-weight-medium">How It Works</h2>
        
        <div class="timeline-container">
          <!-- Step 1 -->
          <div class="timeline-item">
            <div class="timeline-step left-step">
              <div class="step-label">Step 1</div>
            </div>
            <div class="timeline-line">
              <div class="timeline-dot"></div>
            </div>
            <div class="timeline-content right-content">
              <v-card class="timeline-card" variant="elevated" elevation="3">
                <div class="card-accent"></div>
                <v-card-item>
                  <v-icon size="large" color="primary" class="mb-2">mdi-web</v-icon>
                  <v-card-title class="text-black pa-0 text-h5 font-weight-medium">Set Up CMS Integration</v-card-title>
                </v-card-item>
                <v-card-text class="text-black pt-2">
                  Connect your WordPress or Drupal website for automatic content publishing.
                </v-card-text>
              </v-card>
            </div>
          </div>
          
          <!-- Step 3 -->
          <div class="timeline-item">
            <div class="timeline-content left-content">
              <v-card class="timeline-card" variant="elevated" elevation="3">
                <div class="card-accent"></div>
                <v-card-item>
                  <v-icon size="large" color="primary" class="mb-2">mdi-calendar-outline</v-icon>
                  <v-card-title class="text-black pa-0 text-h5 font-weight-medium">Create Publishing Calendar</v-card-title>
                </v-card-item>
                <v-card-text class="text-black pt-2">
                  Schedule when and where your content should be published.
                </v-card-text>
              </v-card>
            </div>
            <div class="timeline-line">
              <div class="timeline-dot"></div>
            </div>
            <div class="timeline-step right-step">
              <div class="step-label">Step 2</div>
            </div>
          </div>
          
          <!-- Step 4 -->
          <div class="timeline-item">
            <div class="timeline-step left-step">
              <div class="step-label">Step 3</div>
            </div>
            <div class="timeline-line">
              <div class="timeline-dot"></div>
            </div>
            <div class="timeline-content right-content">
              <v-card class="timeline-card" variant="elevated" elevation="3">
                <div class="card-accent"></div>
                <v-card-item>
                  <v-icon size="large" color="primary" class="mb-2">mdi-robot</v-icon>
                  <v-card-title class="text-black pa-0 text-h5 font-weight-medium">Sit Back and Relax</v-card-title>
                </v-card-item>
                <v-card-text class="text-black pt-2">
                  Our AI agents will create and publish your content according to your schedule.
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>
      </v-container>
    </section>

    <!-- Pricing Section with modern cards -->
    <section class="pricing py-16 bg-grey-lighten-4 rounded-xl mx-4 my-8">
      <v-container>
        <h2 class="text-h4 text-center mb-12 text-black font-weight-medium">Simple, Transparent Pricing</h2>
        <subscription-plans
          :plans="plans"
          :selected-plan-id="selectedPlanId"
          :column-size="4"
          @select-plan="selectPlan"
        />
      </v-container>
    </section>

    <!-- CTA Section with softer design -->
    <section class="cta py-16 rounded-xl mx-4 my-8 bg-white">
      <v-container>
        <v-row align="center" justify="center">
          <v-col cols="12" md="8" class="text-center">
            <h2 class="text-h4 mb-6 text-black font-weight-medium">Ready to boost your content marketing?</h2>
            <p class="text-body-1 mb-8 text-black">
              Join RankCrew today and let our AI agents create high-quality, SEO-optimized content for your website.
            </p>
            <v-btn
              color="primary"
              size="large"
              to="/register"
              class="mx-2 rounded-xl"
              elevation="1"
              variant="elevated"
            >
              Get Started
            </v-btn>
            <v-btn
              variant="outlined"
              color="primary"
              size="large"
              to="/login"
              class="mx-2 rounded-xl"
            >
              Login
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Footer with cleaner design -->
    <v-footer class="bg-black rounded-t-xl mt-8">
      <v-container>
        <v-row>
          <v-col cols="12" md="4">
            <h3 class="text-h6 text-white mb-4 font-weight-medium">RankCrew</h3>
            <p class="text-body-2 text-white opacity-medium">
              AI-driven SEO content creation service for businesses of all sizes.
            </p>
          </v-col>
          <v-col cols="12" md="4">
            <h3 class="text-h6 text-white mb-4 font-weight-medium">Quick Links</h3>
            <v-list bg-color="transparent" class="pa-0">
              <v-list-item to="/" title="Home" class="pa-0 mb-2 text-white"></v-list-item>
              <v-list-item to="/about" title="About" class="pa-0 mb-2 text-white"></v-list-item>
              <v-list-item to="/pricing" title="Pricing" class="pa-0 mb-2 text-white"></v-list-item>
              <v-list-item to="/login" title="Login" class="pa-0 mb-2 text-white"></v-list-item>
              <v-list-item to="/register" title="Register" class="pa-0 text-white"></v-list-item>
            </v-list>
          </v-col>
          <v-col cols="12" md="4">
            <h3 class="text-h6 text-white mb-4 font-weight-medium">Contact</h3>
            <p class="text-body-2 text-white opacity-medium">
              <v-icon size="small" class="mr-2 text-white">mdi-email</v-icon> hello@rankcrew.ai
            </p>
          </v-col>
        </v-row>
        <v-divider class="my-4 bg-white opacity-medium"></v-divider>
        <div class="text-center text-white text-body-2 opacity-medium">
          &copy; {{ currentYear }} RankCrew. All rights reserved.
        </div>
      </v-container>
    </v-footer>
  </div>
</template>

<script>
import SubscriptionPlans from '@/components/SubscriptionPlans.vue';
import subscriptionService from '@/services/subscriptionService';

export default {
  name: 'HomePage',
  components: {
    SubscriptionPlans
  },
  data() {
    return {
      plans: [],
      loading: true,
      error: null,
      selectedPlanId: null
    };
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    }
  },
  created() {
    this.loadPlans();
  },
  methods: {
    async loadPlans() {
      this.loading = true;
      this.error = null;
      
      try {
        // Use updated local pricing data until backend is updated
        const updatedPlans = [
          {
            id: 2,
            name: 'Basic',
            slug: 'basic',
            posts_per_month: 10,
            price: '99.00',
            stripe_price_id: 'price_1RBEgSFwv0sHNvuZTx8fvWV8',
            is_active: true
          },
          {
            id: 3,
            name: 'Standard',
            slug: 'standard',
            posts_per_month: 50,
            price: '349.00',
            stripe_price_id: 'price_1RBEgTFwv0sHNvuZoSvSVSEY',
            is_active: true
          },
          {
            id: 4,
            name: 'Premium',
            slug: 'premium',
            posts_per_month: 100,
            price: '699.00',
            stripe_price_id: 'price_1RBEgTFwv0sHNvuZcLpW35Gp',
            is_active: true
          }
        ];
        
        // Filter out free plans and use updated pricing
        this.plans = updatedPlans.filter(plan => 
          plan.slug !== 'free' && plan.name !== 'Free' && parseFloat(plan.price) > 0
        );
      } catch (err) {
        console.error('Error loading plans:', err);
        this.error = 'Failed to load subscription plans. Please try again later.';
      } finally {
        this.loading = false;
      }
    },
    
    selectPlan(plan) {
      // Store the selected plan ID and slug in localStorage
      localStorage.setItem('selectedPlanId', plan.id);
      localStorage.setItem('selectedPlanSlug', plan.slug);
      
      // Log the values for debugging
      console.log('Home page - Stored plan ID:', plan.id);
      console.log('Home page - Stored plan slug:', plan.slug);
      
      this.$router.push('/register');
    }
  }
}
</script>

<style scoped>
.hero {
  background: linear-gradient(135deg, #2E7D32 0%, #81C784 100%);
  color: white;
  position: relative;
}

.green-text {
  color: #2E7D32;
}

.text-primary {
  color: #2E7D32 !important;
}

.text-white {
  color: white !important;
}

.text-black {
  color: black !important;
}

/* Opacity variants */
.opacity-high {
  opacity: 0.9;
}

.opacity-medium {
  opacity: 0.7;
}

/* Add more engaging hover effects */
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
}

/* Rounded corners */
.rounded-xl {
  border-radius: 16px !important;
}

.rounded-t-xl {
  border-radius: 16px 16px 0 0 !important;
}

.rounded-b-xl {
  border-radius: 0 0 16px 16px !important;
}

/* Timeline improved styles */
.timeline-improved {
  --timeline-divider-width: 2px !important;
}

.timeline-improved .v-timeline-divider__dot {
  background-color: #2E7D32 !important;
}

.timeline-content {
  max-width: 100%;
}

.border-primary {
  border: 2px solid #2E7D32 !important;
}

/* Mobile adjustments for timeline */
@media (max-width: 959px) {
  .timeline-improved {
    --timeline-line-position: 0px !important;
  }
  
  .timeline-improved .v-timeline-item {
    grid-template-areas: "divider content" !important;
    grid-template-columns: 64px 1fr !important;
  }
  
  .timeline-improved .v-timeline-item__opposite {
    display: none !important;
  }
  
  .timeline-improved .timeline-content {
    padding-left: 12px;
    padding-right: 12px;
  }
  
  .timeline-left, .timeline-right {
    text-align: left;
  }
}

/* Desktop timeline layout */
@media (min-width: 960px) {
  .timeline-left {
    text-align: right;
    padding-right: 24px;
  }
  
  .timeline-right {
    text-align: left;
    padding-left: 24px;
  }
}

/* Remove the previous mobile timeline styles */
.timeline-mobile {
  display: none;
}

/* Custom Timeline Styles - Redesigned */
.timeline-container {
  position: relative;
  padding: 20px 0;
  max-width: 1200px;
  margin: 0 auto;
}

.timeline-container::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 1px;
  background: linear-gradient(180deg, rgba(46, 125, 50, 0.1), rgba(46, 125, 50, 0.8), rgba(46, 125, 50, 0.1));
  transform: translateX(-50%);
}

.timeline-item {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 100px;
  position: relative;
}

.timeline-line {
  position: relative;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.timeline-dot {
  width: 14px;
  height: 14px;
  background: linear-gradient(135deg, #2E7D32, #81C784);
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(46, 125, 50, 0.4);
  z-index: 1;
}

.timeline-step {
  width: 20%;
  padding: 0 15px;
}

.timeline-content {
  width: 38%;
  padding: 0 15px;
}

.left-step {
  text-align: right;
  margin-right: 20px;
}

.right-step {
  text-align: left;
  margin-left: 20px;
}

.left-content {
  text-align: right;
  margin-right: 20px;
}

.right-content {
  text-align: left;
  margin-left: 20px;
}

.step-label {
  color: #2E7D32;
  font-weight: 500;
  font-size: 1.125rem;
  letter-spacing: 0.5px;
  opacity: 0.9;
}

.timeline-card {
  position: relative;
  border-radius: 16px !important;
  overflow: hidden;
  transition: all 0.3s ease;
}

.timeline-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(46, 125, 50, 0.15) !important;
}

.card-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #2E7D32, #81C784);
}

/* Mobile Responsive Timeline */
@media (max-width: 767px) {
  .timeline-container::before {
    left: 30px;
  }
  
  .timeline-item {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 60px;
  }
  
  .timeline-line {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
  }
  
  .timeline-step, .timeline-content {
    width: auto;
  }
  
  .left-step, .right-step {
    margin-left: 60px;
    text-align: left;
    order: 1;
    margin-bottom: 16px;
  }
  
  .left-content, .right-content {
    margin-left: 60px;
    text-align: left;
    order: 2;
    width: calc(100% - 80px);
  }
}

/* Agent Card Styles */
.agents-section {
  background-color: #fafafa;
}

.agent-card {
  border: 1px solid rgba(46, 125, 50, 0.1) !important;
  transition: all 0.25s ease;
}

.agent-card:hover {
  border-color: rgba(46, 125, 50, 0.3) !important;
  background-color: #f5f9f5;
  transform: translateY(-5px);
}

.manager-card {
  position: relative;
  overflow: hidden;
}

.manager-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E");
  z-index: 0;
}

.manager-card > div {
  position: relative;
  z-index: 1;
}

@media (max-width: 599px) {
  .agent-card {
    margin-bottom: 16px;
  }
}
</style> 
